<script setup>
import { useDateFormat } from '@vueuse/core';
import Gallery from './Gallery.vue';
import html2pdf from 'html2pdf.js';
import FaceMatched from '../../assets/icons/facematched.svg';
import Spoofed from '../../assets/icons/smile.svg';

const props = defineProps({
    result: {
        type: Object,
        default: {},
    },
    translation: {
        type: Object,
        default: {},
    },
    qrcode: {
        type: String,
        default: '',
    },
});
const groupImages = (item) => {
    const groups = {};
    const images = item?.before_exam || [];

    images.forEach((img) => {
        const date = useDateFormat(img.created_at, 'DD.MM.YYYY').value;
        if (!groups[date]) {
            groups[date] = [];
        }
        groups[date].push(img);
    });

    return groups;
};

const generatePdf = async () => {
    const element = document.getElementById('pdf-content');
    const options = {
        margin: [0.5, 0.5, 0.5, 0.5],
        filename: `${props.result.full_name}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: {
            dpi: 300,
            scale: 2,
            letterRendering: true,
            useCORS: true,
        },
        // pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
        jsPDF: { unit: 'in', format: 'A4', orientation: 'portrait' },
    };

    document.querySelectorAll('#pdf-content img').forEach((img) => {
        img.style.pageBreakInside = 'avoid';
    });

    await html2pdf().set(options).from(element).save();
};

defineExpose({
    generatePdf,
});
</script>

<template>
    <div class="template-container" id="pdf-content">
        <h1 class="template-title">{{ translation.title }}</h1>
        <div class="template-wrapper">
            <img :src="result.photo" class="template-user-img" />
            <div class="template-info">
                <p>
                    <b>{{ translation.fio }}:</b> {{ result.full_name }}
                </p>
                <p>
                    <b>{{ translation.score }}:</b> {{ result.score }}
                </p>
                <p>
                    <b>{{ translation.passScore }}:</b> {{ result.base_score }}
                </p>
                <p>
                    <b>{{ translation.duration }}:</b> {{ result.duration ? result.duration : '-' }}
                </p>
                <p>
                    <b>{{ translation.start }}:</b> {{ result.started_at ? useDateFormat(result.started_at, 'HH:mm').value : '-' }}
                </p>
                <p>
                    <b>{{ translation.violations }}:</b> <span class="item-value">{{ result.cheating_count ? result.cheating_count : '-' }}</span>
                </p>
                <p>
                    <b>{{ translation.status }}:</b> <span :class="result.status == 'PASSED' ? 'text-green-500' : 'text-red-500'">{{ translation.result }}</span>
                </p>
                <p>
                    <b>{{ translation.sessionName }}:</b> {{ result.name }}
                </p>
                <p>
                    <b>{{ translation.examID }}:</b> {{ result.exam_id }}
                </p>
                <div class="template-qr-code" v-if="result.video && !result.is_video_deleted">
                    <img :src="qrcode" alt="QR Code" />
                    <p>{{ translation.videoScan }}</p>
                </div>
                <p v-else-if="!result.video" class="template-error-text">{{ translation.videoError }}</p>
                <p v-else class="template-error-text">{{ translation.videoDeleted }}</p>
            </div>
        </div>

        <DataTable :value="result.cheatings">
            <template #empty> {{ translation.noData }}</template>
            <Column field="code" :header="translation.code">
                <template #body="{ data }">
                    {{ data.code.toUpperCase() }}
                </template>
            </Column>
            <Column field="code" :header="translation.desc">
                <template #body="{ data }">
                    {{ translation.violationDesc[data.code] }}
                </template>
            </Column>
            <Column field="score" :header="translation.penalty">
                <template #body="{ data }">
                    <span>{{ data.score }}</span>
                </template>
            </Column>
            <Column field="created_at" :header="translation.time">
                <template #body="{ data }">
                    <span>{{ useDateFormat(data.created_at, 'HH:mm:ss').value }}</span>
                </template>
            </Column>
        </DataTable>

        <div class="icons-info-wrapper">
            <div class="template-icons-info">
                <span class="template-icon-wrapper success">
                    <Spoofed class="template-icon" />
                </span>
                <span class="icon-info-text">- {{ translation.isNotSpoofed }}</span>
            </div>

            <div class="template-icons-info">
                <span class="template-icon-wrapper danger">
                    <Spoofed class="template-icon" />
                </span>
                <span class="icon-info-text">- {{ translation.isSpoofed }}</span>
            </div>

            <div class="template-icons-info">
                <span class="template-icon-wrapper danger">
                    <FaceMatched class="template-icon" />
                </span>
                <span class="icon-info-text">- {{ translation.faceNotMatched }}</span>
            </div>

            <div class="template-icons-info">
                <span class="template-icon-wrapper success">
                    <FaceMatched class="template-icon" />
                </span>
                <span class="icon-info-text">- {{ translation.faceMatched }}</span>
            </div>
        </div>

        <div class="template-group-separator">
            <Htag v-if="result.before_exam?.length" tag="h1" class="template-gallery-title"> {{ translation.beforeExam }}: </Htag>

            <div v-for="(images, date) in groupImages(result)" :key="date" class="template-date-group">
                <Htag tag="h2">{{ date }}</Htag>
                <div class="template-gallery-wrapper">
                    <Gallery v-for="img in images" :key="img.created_at" :src="img.image" :isFaceMathed="img.is_face_matched" :isSpoofed="img.is_spoofed" :created="img.created_at" />
                </div>
            </div>
        </div>

        <Htag v-if="result.during_exam?.length" tag="h1" class="template-gallery-title"> {{ translation.duringExam }}: </Htag>

        <div class="template-gallery-wrapper">
            <Gallery v-for="img in result.during_exam" :key="img.created_at" :src="img.image" :isFaceMathed="img.is_face_matched" :isSpoofed="img.is_spoofed" :created="img.created_at" />
        </div>
    </div>
</template>

<style scoped>
.template-wrapper {
    @apply flex gap-8 items-start;
}
.template-user-img {
    @apply w-[250px];
}
.template-title {
    @apply text-2xl font-bold text-center mb-8;
}
.template-info {
    @apply flex flex-col;
}
.template-gallery-wrapper {
    @apply flex flex-wrap gap-4;
}
.template-gallery-title {
    @apply my-4;
}
.template-group-separator {
    page-break-inside: avoid;
    page-break-before: auto;
    page-break-after: auto;
}
.template-date-group {
    @apply my-4 flex flex-col gap-2;
}
.template-qr-code {
    @apply flex items-center gap-4 mt-2;
}
.template-qr-code img {
    @apply w-[100px] h-[100px];
}
.template-qr-code p {
    @apply mb-4;
}
.template-error-text {
    @apply text-red-500 mt-[5px];
}
.template-icons-info {
    @apply flex items-center gap-2;
}
.icons-info-wrapper {
    @apply flex flex-col mt-2;
}
.template-icon-wrapper {
    @apply w-[20px] h-[20px] flex justify-center items-center rounded-full;
}
.template-icon-wrapper.success {
    @apply bg-[#34C759];
}
.template-icon-wrapper.danger {
    @apply bg-[#FF4747];
}
.icon-info-text {
    @apply pb-4;
}
:deep(.template-icon) {
    @apply w-4 h-4 text-white;
}
</style>
